import { NavLink, Outlet } from 'react-router-dom';
import { isDemoSession, clearDemoSession } from '../lib/api';
import '../styles/AppShell.css';

/** Primary nav — desktop sidebar + mobile bottom bar */
const NAV_ITEMS = [
  { to: '/', label: 'Hem', end: true },
  { to: '/find', label: 'Hitta' },
  { to: '/remove', label: 'Ta bort' },
  { to: '/monitor', label: 'Bevaka' },
  { to: '/account', label: 'Konto' },
] as const;

/** Extra desktop-only items (not in bottom nav) */
const SIDEBAR_EXTRA = [
  { to: '/family', label: 'Familj' },
  { to: '/lexbase', label: 'Lexbase' },
] as const;

function linkClass(isActive: boolean, base: string, active: string) {
  return isActive ? `${base} ${active}` : base;
}

export function AppShell() {
  const demo = typeof window !== 'undefined' && isDemoSession();

  return (
    <div className="app-shell">
      {demo && (
        <div
          role="status"
          style={{
            gridColumn: '1 / -1',
            background: '#fff3e4',
            borderBottom: '1px solid #D4AF37',
            color: '#7d5411',
            fontSize: 13,
            padding: '8px 16px',
            textAlign: 'center',
            zIndex: 50,
          }}
        >
          <strong>PUBLIK DEMO</strong> — Anna Exempel (fiktiv). Inget konto.{" "}
          <button
            type="button"
            onClick={() => {
              clearDemoSession();
              window.location.href = 'https://gdpr-nordic.org/demo';
            }}
            style={{
              marginLeft: 8,
              background: 'transparent',
              border: '1px solid #7d5411',
              borderRadius: 4,
              color: '#7d5411',
              cursor: 'pointer',
              fontSize: 12,
              padding: '2px 8px',
            }}
          >
            Avsluta demo
          </button>
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className="app-sidebar" aria-label="Huvudmeny">
        <div className="app-sidebar__brand">
          <img src="/favicon.png" alt="" width={32} height={32} />
          <span>GDPR Nordic</span>
        </div>
        <nav className="app-sidebar__nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) =>
                linkClass(
                  isActive,
                  'app-sidebar__link',
                  'app-sidebar__link--active',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          {SIDEBAR_EXTRA.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                linkClass(
                  isActive,
                  'app-sidebar__link',
                  'app-sidebar__link--active',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="app-sidebar__footer">app.gdpr-nordic.org</div>
      </aside>

      {/* Main column */}
      <div className="app-main">
        <header className="app-main__header">
          <span className="app-main__header-brand">GDPR Nordic</span>
        </header>
        <main className="app-main__content">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav — Hem · Hitta · Ta bort · Bevaka · Konto */}
      <nav className="app-bottom-nav" aria-label="Mobil navigering">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={'end' in item ? item.end : false}
            className={({ isActive }) =>
              linkClass(
                isActive,
                'app-bottom-nav__link',
                'app-bottom-nav__link--active',
              )
            }
          >
            <span className="app-bottom-nav__icon" aria-hidden>
              {iconFor(item.to)}
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** Minimal text icons for bottom nav (agents 6–7 can swap for SVG) */
function iconFor(path: string): string {
  switch (path) {
    case '/':
      return '⌂';
    case '/find':
      return '⌕';
    case '/remove':
      return '⌫';
    case '/monitor':
      return '◎';
    case '/account':
      return '○';
    default:
      return '·';
  }
}

export default AppShell;
